import React, { useMemo } from "react";
import { useTranslation } from 'react-i18next'
const clamp = (n) => Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));

const MetricPill = ({ label, value, hint, tone, isDark }) => {
  const v = clamp(value);
  const ring = `conic-gradient(var(--tone) ${v}%, var(--track) 0)`;

  return (
    <div
      className={`flex items-center gap-3 rounded-xl px-3 py-2 ring-1 ${
        isDark ? "ring-white/10" : "ring-black/10"
      }`}
      style={{
        // variable CSS locale pour te laisser changer facilement les couleurs
        ["--tone"]: tone,
        ["--track"]: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
      }}
    >
      {/* Mini ring */}
      <div
        className="relative h-10 w-10 rounded-full"
        style={{ background: ring }}
        aria-hidden
      >
        <div
          className={`absolute inset-[5px] rounded-full flex items-center justify-center ${
            isDark ? "bg-gray-900" : "bg-gray-100"
          }`}
        >
          <span className="text-[11px] font-semibold tabular-nums">
            {v.toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Text */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            {label}
          </div>

          {/* Accent dot */}
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: tone, opacity: isDark ? 0.9 : 1 }}
            aria-hidden
          />
        </div>

        <div className="mt-0.5 flex items-baseline justify-between gap-2">
          <div className={`text-base font-bold tabular-nums leading-none ${isDark ? "text-gray-100" : "text-gray-900"}`}>
            {clamp(value).toFixed(1)}%
          </div>

          {hint ? (
            <div className={`text-[11px] tabular-nums ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {hint}
            </div>
          ) : (
            <div className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-500"}`}> </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TinyTrend = ({ daily, isDark, tone }) => {
  const vals = (Array.isArray(daily) ? daily : [])
    .slice(-10)
    .map((d) => clamp(Number(d?.percent)));

  const max = Math.max(1, ...vals);
  return (
    <div className="flex items-end gap-1 h-6" aria-hidden>
      {vals.map((v, i) => (
        <div
          key={i}
          className="w-1.5 rounded-sm"
          style={{
            height: `${Math.round((v / max) * 100)}%`,
            backgroundColor: tone,
            opacity: isDark ? 0.28 : 0.22,
          }}
        />
      ))}
    </div>
  );
};

export default function AvailabilityCompactCard({ data, isDarkMode }) {
  const { t } = useTranslation() 
  const currentPct = Number(data?.current?.percent ?? 0);
  const avg7 = Number(data?.last_7_days?.avg_percent ?? 0);
  const avg30 = Number(data?.last_30_days?.avg_percent ?? 0);

  const total = Number(data?.current?.total ?? data?.total_players ?? 0);
  const absent = Number(data?.current?.absent ?? 0);
  const available = Number(data?.current?.available ?? 0);

  const delta7 = useMemo(() => clamp(currentPct) - clamp(avg7), [currentPct, avg7]);

  // Tons (3 couleurs distinctes)
  const TONES = {
    now:  isDarkMode ? "#60a5fa" : "#2563eb", // blue
    d7:   isDarkMode ? "#34d399" : "#059669", // emerald
    d30:  isDarkMode ? "#a78bfa" : "#7c3aed", // violet
  };

  const deltaLabel =
    Math.abs(delta7) < 0.1 ? "±0.0 pts" : `${delta7 >= 0 ? "+" : ""}${delta7.toFixed(1)} pts`;

  return (
    <div className="w-full">
      {/* Header ultra léger */}
      <div className="flex items-end justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className={`text-sm font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>
            {t('availability.title')}
          </div>
          <div className={`text-[12px] ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            {total ? `${available}/${total} ${t('availability.available')} · ${absent} ${t('availability.out')}` : "—"}
          </div>
        </div>

        {/* Mini trend discret (7 derniers jours) */}
        <div className="flex flex-col items-end gap-1">
          <div className={`text-[10px] uppercase tracking-wide ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
           {t('availability.trend')}
          </div>
          <TinyTrend daily={data?.last_7_days?.daily} isDark={isDarkMode} tone={TONES.d7} />
        </div>
      </div>

      {/* 3 métriques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MetricPill
          label={t('availability.now')}
          value={currentPct}
          hint={data?.current?.date ? data.current.date : ""}
          tone={TONES.now}
          isDark={isDarkMode}
        />
        <MetricPill
          label={` ${t('availability.avg7')}`}
          value={avg7}
          hint={deltaLabel}
          tone={TONES.d7}
          isDark={isDarkMode}
        />
        <MetricPill
          label={`${t('availability.avg30')}`}
          value={avg30}
          hint={data?.last_30_days?.from && data?.last_30_days?.to ? `${data.last_30_days.from} → ${data.last_30_days.to}` : ""}
          tone={TONES.d30}
          isDark={isDarkMode}
        />
      </div>
    </div>
  );
}
